import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import { TrendingUp, Clock, DollarSign, Target, CheckCircle, ArrowRight } from "lucide-react";
import seoBeforeResults from "@/assets/seo-before-caching.png";
import seoAfterResults from "@/assets/seo-after-results-updated.png";

const SEOCaseStudy = () => {
  const beforeStats = [
    { label: "Data Cached", value: "8%", status: "poor" },
    { label: "Daily Unique Visitors", value: "10", status: "poor" },
    { label: "Search Visibility", value: "Poor", status: "poor" },
    { label: "Local Directory Presence", value: "Minimal", status: "poor" }
  ];

  const afterStats = [
    { label: "Data Cached", value: "100%", status: "excellent" },
    { label: "Daily Unique Visitors", value: "40+", status: "excellent" },
    { label: "Search Visibility", value: "Excellent", status: "excellent" },
    { label: "Local Directory Presence", value: "Complete", status: "excellent" }
  ];

  const improvements = [
    {
      icon: TrendingUp,
      title: "1,250% Caching Improvement",
      description: "From 8% to 100% data cached for maximum performance optimization"
    },
    {
      icon: Clock,
      title: "4x Visitor Growth in 4 Days",
      description: "Daily unique visitors jumped from 10 to 40+ through strategic SEO and social media embedding"
    },
    {
      icon: DollarSign,
      title: "Complete Local Optimization",
      description: "Transformed from minimal presence to complete local directory coverage"
    },
    {
      icon: Target,
      title: "Strategic Meta Tag Integration",
      description: "Optimized H1 tags and social media embedding for maximum visibility"
    }
  ];

  return (
    <>
      <EnhancedSEO 
        title="Local SEO Case Study Results - Vincent Growth Partners"
        description="See how we transformed a service business from 8% to 100% local visibility, generating 13x more leads through strategic SEO optimization."
        keywords="local seo results, case study, business growth, lead generation, search rankings"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-6 text-lg px-6 py-2">
              REAL CLIENT RESULTS
            </Badge>
            <h1 className="text-5xl md:text-8xl font-black mb-8 text-foreground leading-tight">
              From <span className="text-destructive text-6xl md:text-9xl">8%</span> to <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent text-6xl md:text-9xl">100%</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed font-semibold">
              Complete transformation captured in one powerful image - from 8% to 100% data caching 
              and <span className="text-primary font-bold">4x visitor growth</span> achieved in just <span className="text-secondary font-bold">4 days</span> through strategic SEO optimization.
            </p>
          </div>


          {/* Key Improvements - Bold & Punchy */}
          <div className="mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-center mb-12 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Key Improvements Achieved
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Caching Improvement */}
              <Card className="hover:shadow-2xl transition-all hover:scale-105 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <TrendingUp className="w-10 h-10 text-white" />
                    </div>
                    <div>
                      <div className="text-5xl md:text-6xl font-black text-primary mb-2">1,250%</div>
                      <h3 className="font-bold text-2xl mb-3 text-foreground">Caching Improvement</h3>
                      <p className="text-lg text-muted-foreground font-medium">
                        From <span className="text-destructive font-bold">8%</span> to <span className="text-primary font-bold">100%</span> data cached for maximum performance optimization
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Visitor Growth */}
              <Card className="hover:shadow-2xl transition-all hover:scale-105 border-2 border-secondary/20 bg-gradient-to-br from-secondary/5 to-transparent">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-secondary to-accent rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Clock className="w-10 h-10 text-white" />
                    </div>
                    <div>
                      <div className="text-5xl md:text-6xl font-black text-secondary mb-2">4x</div>
                      <h3 className="font-bold text-2xl mb-3 text-foreground">Visitor Growth in 4 Days</h3>
                      <p className="text-lg text-muted-foreground font-medium">
                        Daily unique visitors jumped from <span className="text-destructive font-bold">10</span> to <span className="text-secondary font-bold">40+</span> through strategic SEO and social media embedding
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Local Optimization */}
              <Card className="hover:shadow-2xl transition-all hover:scale-105 border-2 border-accent/20 bg-gradient-to-br from-accent/5 to-transparent">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-accent to-primary rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <DollarSign className="w-10 h-10 text-white" />
                    </div>
                    <div>
                      <div className="text-4xl md:text-5xl font-black text-accent mb-2">Complete</div>
                      <h3 className="font-bold text-2xl mb-3 text-foreground">Local Optimization</h3>
                      <p className="text-lg text-muted-foreground font-medium">
                        Transformed from <span className="text-destructive font-bold">minimal presence</span> to <span className="text-accent font-bold">complete local directory coverage</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Strategic Integration */}
              <Card className="hover:shadow-2xl transition-all hover:scale-105 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Target className="w-10 h-10 text-white" />
                    </div>
                    <div>
                      <div className="text-4xl md:text-5xl font-black text-primary mb-2">Strategic</div>
                      <h3 className="font-bold text-2xl mb-3 text-foreground">Meta Tag Integration</h3>
                      <p className="text-lg text-muted-foreground font-medium">
                        Optimized H1 tags and social media embedding for <span className="text-primary font-bold">maximum visibility</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Timeline */}
          <Card className="mb-16">
            <CardHeader>
              <CardTitle className="text-center">Rapid 7-Day Launch Timeline</CardTitle>
              <p className="text-center text-muted-foreground">From concept to live website in one week</p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold">Day 1: Demo Built on Test Domain</h4>
                    <p className="text-muted-foreground">Complete website demo ready for client review with SEO foundation</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold">Day 2: Client Confirmation & Adjustments</h4>
                    <p className="text-muted-foreground">Client reviews, confirms satisfaction, and suggests improvements</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold">Week 1: Live Launch on Client Domain</h4>
                    <p className="text-muted-foreground">Suggestions implemented, SEO optimized, and website launched live</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white font-bold text-sm">
                    ∞
                  </div>
                  <div>
                    <h4 className="font-semibold">Ongoing: Weekly Reviews & Marketing Strategy</h4>
                    <p className="text-muted-foreground">Weekly website reviews, marketing guidance, and funnel optimization</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Compounding Growth Section */}
          <div className="mb-16">
            <div className="bg-gradient-to-br from-secondary/10 via-primary/10 to-accent/10 rounded-3xl p-8 md:p-12 border-2 border-primary/20">
              <div className="text-center mb-10">
                <h2 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                  Results Compound After Launch
                </h2>
                <p className="text-xl md:text-2xl text-muted-foreground font-semibold">
                  The 7-day launch is just the beginning. Stay with us and watch your growth accelerate exponentially.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {/* Month 1 */}
                <Card className="border-2 border-primary/30 bg-background/80 backdrop-blur hover:scale-105 transition-transform">
                  <CardContent className="p-6 text-center">
                    <div className="text-5xl font-black text-primary mb-3">Month 1</div>
                    <h3 className="text-2xl font-bold mb-4 text-foreground">Foundation Set</h3>
                    <ul className="text-left space-y-2 text-muted-foreground">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span>4x visitor growth achieved</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span>100% data caching implemented</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span>Local SEO fully optimized</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Month 3 */}
                <Card className="border-2 border-secondary/30 bg-background/80 backdrop-blur hover:scale-105 transition-transform">
                  <CardContent className="p-6 text-center">
                    <div className="text-5xl font-black text-secondary mb-3">Month 3</div>
                    <h3 className="text-2xl font-bold mb-4 text-foreground">Momentum Builds</h3>
                    <ul className="text-left space-y-2 text-muted-foreground">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-secondary mr-2 flex-shrink-0 mt-0.5" />
                        <span>10x organic traffic increase</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-secondary mr-2 flex-shrink-0 mt-0.5" />
                        <span>Keywords ranking on page 1</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-secondary mr-2 flex-shrink-0 mt-0.5" />
                        <span>Lead quality improving</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Month 6+ */}
                <Card className="border-2 border-accent/30 bg-background/80 backdrop-blur hover:scale-105 transition-transform">
                  <CardContent className="p-6 text-center">
                    <div className="text-5xl font-black text-accent mb-3">Month 6+</div>
                    <h3 className="text-2xl font-bold mb-4 text-foreground">Exponential Growth</h3>
                    <ul className="text-left space-y-2 text-muted-foreground">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                        <span>20x+ organic traffic growth</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                        <span>Dominating local search results</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent mr-2 flex-shrink-0 mt-0.5" />
                        <span>Consistent high-value leads</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <div className="text-center bg-gradient-to-r from-primary/20 to-secondary/20 rounded-2xl p-8">
                <p className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  The Secret? <span className="text-primary">Consistency</span> + <span className="text-secondary">Expert Optimization</span>
                </p>
                <p className="text-lg text-muted-foreground font-medium">
                  SEO compounds like interest. The longer you stay, the stronger your position becomes. 
                  Our clients who stick with us see <span className="text-primary font-bold">exponential growth</span> while competitors struggle to catch up.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <Card className="bg-gradient-to-r from-primary to-primary-light text-white">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-accent" />
              <h2 className="text-3xl font-bold mb-4">Ready for Similar Results?</h2>
              <p className="text-xl mb-6 text-white/90">
                This isn't a one-off success. We deliver these results consistently for service-based businesses.
              </p>
              <Button 
                variant="secondary" 
                size="lg"
                className="bg-white text-primary hover:bg-white/90"
                asChild
              >
                <a href="/do-you-qualify">
                  Get Your Free SEO Audit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default SEOCaseStudy;